import type { ReaderAiActionType, ToofiProductIntent, ToofiProductMode } from "@/types/agentic-chat";

export const TOOFI_MODE_STARTER_PROMPTS: Record<ToofiProductMode, string[]> = {
  learn: ["Explain this step by step", "Make this simpler", "Give me an example"],
  practice: ["Quiz me on this", "Give me an essay prompt", "Check my understanding"],
  review: ["What are my weak spots here?", "Give me a revision summary", "Explain what I should revise next"],
  plan: ["Build me a study plan", "What should I do today?", "Help me recover this plan"],
  create: ["Turn this into notes", "Compare the big ideas", "Build a revision map"],
};

const includesAny = (value: string, checks: string[]) => checks.some((check) => value.includes(check));

export const inferToofiProductIntent = ({
  mode,
  prompt,
}: {
  mode: ToofiProductMode | null;
  prompt?: string | null;
}): ToofiProductIntent | null => {
  const normalized = (prompt ?? "").trim().toLowerCase();
  if (!normalized) return null;
  if (!mode) return null;

  switch (mode) {
    case "learn":
      if (includesAny(normalized, ["step by step", "step-by-step", "بالتدريج", "خطوة خطوة"])) return "step_by_step";
      if (includesAny(normalized, ["simpl", "simpler", "ابسط", "بسّط"])) return "simplify";
      if (includesAny(normalized, ["translate", "translation", "ترجم", "ترجمة"])) return "translate_understand";
      if (includesAny(normalized, ["example", "مثال"])) return "worked_example";
      return null;
    case "practice":
      if (includesAny(normalized, ["essay", "مقال", "essay prompt"])) return "essay_prompt";
      if (includesAny(normalized, ["mistake", "wrong", "غلط", "أخطاء"])) return "mistake_review";
      if (includesAny(normalized, ["quiz", "mcq", "اختبر", "سؤال"])) return "quiz_me";
      return null;
    case "review":
      if (includesAny(normalized, ["report", "تقرير"])) return "report_followup";
      if (includesAny(normalized, ["deep", "comprehensive", "شامل", "عميق"])) return "deep_review";
      if (includesAny(normalized, ["weak", "weak spot", "ضعيف", "نقطة ضعف", "revise next"])) return "weak_spot_review";
      return null;
    case "plan":
      if (includesAny(normalized, ["exam", "sprint", "امتحان"])) return "exam_sprint";
      if (includesAny(normalized, ["recover", "behind", "catch up", "متأخر", "ألحق"])) return "plan_recovery";
      if (includesAny(normalized, ["refine", "adjust", "change", "عدّل", "طور"])) return "plan_refine";
      if (includesAny(normalized, ["today", "today's", "todays", "النهاردة", "اليوم"])) return "daily_plan";
      if (includesAny(normalized, ["plan", "schedule", "roadmap", "خطة", "جدول"])) return "build_plan";
      return null;
    case "create":
      if (includesAny(normalized, ["compare", "comparison", "قارن", "مقارنة"])) return "compare";
      if (includesAny(normalized, ["note", "notes", "ملاحظات", "نوت"])) return "note_create";
      if (includesAny(normalized, ["rewrite", "redesign", "rewrite this", "أعد", "صياغ"])) return "rewrite";
      if (includesAny(normalized, ["revision map", "map", "خريطة", "map out"])) return "revision_map";
      if (includesAny(normalized, ["synthesize", "deeply", "big ideas", "حلل", "استخلص"])) return "deep_synthesis";
      return null;
    default:
      return null;
  }
};

export const mapReaderActionToProductMode = (action: ReaderAiActionType): ToofiProductMode | null => {
  switch (action) {
    case "explain":
    case "follow_up":
    case "simplify":
    case "deepen":
    case "translate":
    case "retranslate":
    case "page_chat":
      return "learn";
    case "context_chat":
    case "general_chat":
      return null;
    case "example":
      return "learn";
    case "quiz":
    case "quiz_answer":
    case "essay_prompt":
    case "essay_evaluate":
    case "mistake_review":
      return "practice";
    case "review_weak_topic":
      return "review";
    case "plan":
      return "plan";
    case "note_create":
    case "organize":
    case "redesign":
    case "summarize":
      return "create";
    default:
      return null;
  }
};

export const mapReaderActionToProductIntent = (action: ReaderAiActionType): ToofiProductIntent | null => {
  switch (action) {
    case "explain":
    case "follow_up":
    case "page_chat":
      return "step_by_step";
    case "simplify":
      return "simplify";
    case "deepen":
      return "worked_example";
    case "translate":
    case "retranslate":
      return "translate_understand";
    case "example":
      return "worked_example";
    case "quiz":
      return "quiz_me";
    case "essay_prompt":
      return "essay_prompt";
    case "mistake_review":
      return "mistake_review";
    case "review_weak_topic":
      return "weak_spot_review";
    case "plan":
      return "build_plan";
    case "note_create":
      return "note_create";
    case "organize":
    case "redesign":
      return "rewrite";
    case "summarize":
      return "revision_map";
    default:
      return null;
  }
};
